import React from 'react';
// import PropTypes from 'prop-types';
import PhoneNumberInput from 'react-phone-number-input';
import { parsePhoneNumber } from 'react-phone-number-input';
import localeRU from 'react-phone-number-input/locale/ru';
import 'react-phone-number-input/style.css';

import { useGlobal } from '_core/hooks/useGlobal';
import { TextInput } from '_core/components/TextInput';
import * as component from '_core/utils/component';

// style
import { useStyle } from './style';

// Функция для определения страны по номеру телефона
// Абхазия: +7 840, +7 940 (использует код +7, поэтому определяется как RU)
// Казахстан: +77xx, +76xx, +70xx, +7313, +7327, +7312, +7321, +7324, +7336, +7318, +7315, +7325, +7311, +7326, +7310
// Остальные +7 - Россия
const detectCountryFromPhone = (phoneValue) => {
  if (!phoneValue || typeof phoneValue !== 'string') {
    return null;
  }

  // Убираем все символы кроме цифр и знака +
  const digits = phoneValue.replace(/[^\d+]/g, '');
  
  // Проверяем, начинается ли номер с +7 или просто 7
  if (digits.startsWith('+7') || digits.startsWith('7')) {
    // Убираем +7 и оставляем только цифры после +7 для проверки
    const cleanDigits = digits.replace(/^\+?7/, '');
    
    if (cleanDigits.length >= 3) {
      // Проверяем коды Абхазии (первые 3 цифры после +7)
      // Абхазия использует код +7, поэтому определяется как RU
      const threeDigits = cleanDigits.substring(0, 3);

      // Проверяем специальные коды городов Казахстана (первые 3 цифры после +7)
      const kazakhCityCodes = ['313', '327', '312', '321', '324', '336', '318', '315', '325', '311', '326', '310'];
      if (kazakhCityCodes.includes(threeDigits)) {
        return 'KZ';
      }
    }
    
    if (cleanDigits.length >= 1) {
      const firstDigit = cleanDigits[0];
      
      // Проверяем префиксы: +77xx, +76xx, +70xx (первая цифра после +7)
      if (firstDigit === '6' || firstDigit === '7' || firstDigit === '0') {
        return 'KZ';
      }
      
      // Остальные номера +7 - Россия
      return 'RU';
    }
  }
  
  return null;
};

// Компонент input для PhoneNumberInput
const InputComponent = React.forwardRef(
  (
    {
      inputComponentRef,
      inputClassName,
      inputOnFocus,
      inputOnBlur,
      inputOnChange,
      ...props
    },
    ref
  ) => (
    <input
      ref={component.ref(ref, inputComponentRef)}
      {...props}
      className={component.className(props.className, inputClassName)}
      onFocus={component.eventProxy(props.onFocus, inputOnFocus)}
      onBlur={component.eventProxy(props.onBlur, inputOnBlur)}
      onChange={component.eventProxy(props.onChange, inputOnChange)}
    />
  )
);

InputComponent.displayName = 'InputComponent';

// Компонент для интеграции с Material-UI TextField
const MuiInputComponent = React.forwardRef(
  ({ className, inputRef, onFocus, onBlur, onChange, ...props }, ref) => {
    // Все пропсы из inputProps.inputProps приходят напрямую в этот компонент через props
    // onChange должен быть в props, так как он передается через inputProps.inputProps
    const { onChange: propsOnChange, ...restProps } = props;
    const handleChange = propsOnChange || onChange;
    
    // Убеждаемся, что onChange всегда является функцией
    const safeOnChange = typeof handleChange === 'function' ? handleChange : (() => {});
    
    // Извлекаем defaultCountry и country из props для использования
    // Также извлекаем normalizedValue, если он передан (для правильной нормализации значения)
    const { defaultCountry: propsDefaultCountry, country: propsCountry, value: propsValue, normalizedValue: propsNormalizedValue, forceUpdateKey: propsForceUpdateKey, initialValueKey: propsInitialValueKey, ...restPropsWithoutDefault } = restProps;
    const effectiveDefaultCountry = propsDefaultCountry || 'RU';
    // Используем country из props, если он есть, иначе defaultCountry
    // country должен быть установлен, чтобы библиотека могла отобразить флаг
    // Важно: country должен быть всегда определен, даже если value пустое
    const effectiveCountry = propsCountry !== undefined && propsCountry !== null ? propsCountry : (propsDefaultCountry || 'RU');
    
    // Создаем обертку для onChange, которая будет передавать countryData
    const handlePhoneChange = React.useCallback((newValue, countryData) => {
      // Передаем оба параметра в safeOnChange
      if (typeof safeOnChange === 'function') {
        safeOnChange(newValue, countryData);
      }
    }, [safeOnChange]);
    
    // Используем key для принудительного обновления компонента при изменении страны
    // Это гарантирует, что флаг обновится при изменении страны
    // Также используем forceUpdateKey для принудительного обновления при инициализации
    // Включаем initialValueKey для принудительного пересоздания при инициализации с заполненным значением
    // Используем country, forceUpdateKey и initialValueKey для гарантированного обновления
    // Если есть начальное значение, включаем его в key для принудительного пересоздания
    // Убеждаемся, что country всегда передается правильно
    // Если country не определен, используем defaultCountry
    const finalCountry = effectiveCountry || effectiveDefaultCountry || 'RU';
    
    // Используем key только для инициализации с заполненным значением
    // Не используем key для обычного ввода, чтобы избежать потери фокуса
    // при изменении страны во время ввода
    const componentKey = propsInitialValueKey && propsInitialValueKey !== 'no-init'
      ? `phone-init-${propsInitialValueKey}-${finalCountry}-${propsForceUpdateKey || 0}`
      : undefined;
    
    return (
      <PhoneNumberInput
        {...(componentKey ? { key: componentKey } : {})}
        ref={component.ref(ref, inputRef)}
        {...restPropsWithoutDefault}
        inputClassName={className}
        inputOnFocus={onFocus}
        inputOnBlur={onBlur}
        inputOnChange={handlePhoneChange}
        inputComponent={InputComponent}
        onChange={handlePhoneChange}
        defaultCountry={effectiveDefaultCountry}
        country={finalCountry}
        value={propsNormalizedValue || propsValue}
        flagUrl="/data/images/global/icons/flags/{XX}.svg"
      />
    );
  }
);

MuiInputComponent.displayName = 'MuiInputComponent';

export const RawPhoneInput = (props) => {
  const classes = useStyle();

  const {
    config: { PHONE_INPUT_PROPS }
  } = useGlobal();

  // Извлекаем пропсы для TextInput и PhoneNumberInput
  const { 
    onChange, 
    value,
    label,
    error,
    helperText,
    checking,
    onBlur,
    ...restProps 
  } = props;

  // Нормализуем номер телефона для определения страны
  // Нормализуем только номера, которые явно являются российскими/казахскими/абхазскими
  // (начинаются с 7 или 8), не трогаем номера других стран
  const normalizedValue = React.useMemo(() => {
    if (!value) return '';
    const normalized = String(value).trim();
    
    // Если номер уже начинается с +, оставляем как есть
    if (normalized.startsWith('+')) {
      return normalized;
    }
    
    // Нормализуем только номера, которые явно являются российскими/казахскими/абхазскими
    // (начинаются с 7 или 8)
    if (normalized.startsWith('7')) {
      return '+' + normalized;
    } else if (normalized.startsWith('8')) {
      return '+7' + normalized.substring(1);
    }
    
    // Для остальных номеров (которые не начинаются с +, 7 или 8)
    // пробуем определить страну через parsePhoneNumber
    // Если страна определена, добавляем + для правильного формата
    if (normalized.length >= 6) {
      try {
        const phoneNumber = parsePhoneNumber('+' + normalized);
        if (phoneNumber && phoneNumber.country) {
          // Страна определена, возвращаем номер с +
          return '+' + normalized;
        }
      } catch (e) {
        // Игнорируем ошибки парсинга
      }
    }
    
    // Если не удалось определить страну, возвращаем как есть
    // Библиотека попробует определить страну самостоятельно
    return normalized;
  }, [value]);

  // Определяем начальную страну на основе value, если оно есть
  // Используем функцию-инициализатор для useState, чтобы определить страну при первом рендере
  const [currentCountry, setCurrentCountry] = React.useState(() => {
    // Используем value напрямую для инициализации, так как normalizedValue может быть еще не вычислен
    const initialValue = value ? String(value).trim() : '';
    if (initialValue) {
      // Нормализуем значение для определения страны
      let normalized = initialValue;
      if (!normalized.startsWith('+')) {
        if (normalized.startsWith('7')) {
          normalized = '+' + normalized;
        } else if (normalized.startsWith('8')) {
          normalized = '+7' + normalized.substring(1);
        } else {
          // Для других номеров пробуем добавить + если его нет
          normalized = '+' + normalized;
        }
      }
      
      // Сначала пробуем определить через нашу функцию
      const detectedCountry = detectCountryFromPhone(normalized);
      if (detectedCountry) {
        return detectedCountry;
      }
      // Если не получилось, пробуем через parsePhoneNumber
      try {
        const phoneNumber = parsePhoneNumber(normalized);
        if (phoneNumber && phoneNumber.country) {
          return phoneNumber.country;
        }
      } catch (e) {
        // Игнорируем ошибки парсинга
      }
    }
    return PHONE_INPUT_PROPS?.defaultCountry?.toUpperCase() || 'RU';
  });

  // Обработчик onChange для автоматического определения страны
  const handleChange = React.useCallback((newValue, countryData) => {
    // Обрабатываем случай, когда значение может быть undefined или null
    const phoneValue = newValue || '';
    
    // Помечаем, что это пользовательский ввод
    isUserInputRef.current = true;
    
    // Если пользователь выбрал страну через UI библиотеки, используем её
    // countryData содержит информацию о выбранной стране
    if (countryData && countryData.country) {
      // Обновляем страну немедленно, чтобы флаг и префикс обновились
      setCurrentCountry((prevCountry) => {
        if (countryData.country !== prevCountry) {
          return countryData.country;
        }
        return prevCountry;
      });
    } else {
      // Иначе определяем страну по номеру автоматически
      const detectedCountry = detectCountryFromPhone(phoneValue);
      
      // Обновляем страну, если она определена
      if (detectedCountry) {
        setCurrentCountry((prevCountry) => {
          if (detectedCountry !== prevCountry) {
            return detectedCountry;
          }
          return prevCountry;
        });
      }
    }
    
    // Передаем значение дальше
    // FormValidator использует getPhoneFieldEventData, который ожидает строку
    // react-phone-number-input передает значение как строку, что правильно
    if (typeof onChange === 'function') {
      onChange(phoneValue);
    }
  }, [onChange]);

  // Определяем страну при изменении value из props
  // Используем useRef для отслеживания, был ли это пользовательский ввод
  const isUserInputRef = React.useRef(false);
  
  React.useEffect(() => {
    // Пропускаем обновление страны, если это пользовательский ввод
    // handleChange уже обрабатывает определение страны при вводе
    if (isUserInputRef.current) {
      isUserInputRef.current = false;
      return;
    }
    
    if (normalizedValue) {
      // Сначала пробуем определить через нашу функцию (для +7 номеров)
      let detectedCountry = detectCountryFromPhone(normalizedValue);
      
      // Если не получилось, пробуем через parsePhoneNumber
      if (!detectedCountry) {
        try {
          // Пробуем парсить как есть
          let phoneNumber = null;
          try {
            phoneNumber = parsePhoneNumber(normalizedValue);
          } catch (e) {
            // Если не получилось и номер не начинается с +, пробуем добавить +
            if (!normalizedValue.startsWith('+')) {
              try {
                phoneNumber = parsePhoneNumber('+' + normalizedValue);
              } catch (e2) {
                // Игнорируем ошибки
              }
            }
          }
          
          if (phoneNumber && phoneNumber.country) {
            detectedCountry = phoneNumber.country;
          }
        } catch (e) {
          // Игнорируем ошибки парсинга
        }
      }
      
      // Обновляем страну, если она определена и отличается от текущей
      if (detectedCountry && detectedCountry !== currentCountry) {
        setCurrentCountry(detectedCountry);
      }
    } else if (!normalizedValue && currentCountry !== (PHONE_INPUT_PROPS?.defaultCountry?.toUpperCase() || 'RU')) {
      // Если value очищен, возвращаемся к стране по умолчанию
      setCurrentCountry(PHONE_INPUT_PROPS?.defaultCountry?.toUpperCase() || 'RU');
    }
  }, [normalizedValue, currentCountry]);
  
  // Используем key для принудительного обновления компонента при инициализации с заполненным значением
  // Это гарантирует, что флаг отобразится при открытии формы с заполненным телефоном
  // Используем начальное значение value в key, чтобы компонент пересоздался с правильным country и value
  const initialValueKey = React.useMemo(() => {
    // Используем первые 15 символов значения для создания уникального ключа
    return value ? `init-${String(value).substring(0, 15).replace(/[^a-zA-Z0-9]/g, '')}` : 'no-init';
  }, []); // Только при монтировании
  
  // Используем ref для отслеживания, был ли компонент уже инициализирован
  const isInitializedRef = React.useRef(false);
  
  // Принудительно обновляем компонент после монтирования, если есть заполненное значение
  const [forceUpdateKey, setForceUpdateKey] = React.useState(0);
  
  // Принудительно обновляем компонент при монтировании, если есть заполненное значение
  // Это гарантирует, что флаг отобразится при открытии формы с заполненным телефоном
  React.useEffect(() => {
    if (normalizedValue && currentCountry && !isInitializedRef.current) {
      // Помечаем, что компонент инициализирован
      isInitializedRef.current = true;
      // Немедленно обновляем ключ, чтобы компонент пересоздался с правильным country
      setForceUpdateKey(1);
      // Также используем несколько попыток с разными задержками для надежности
      const timers = [
        setTimeout(() => {
          setForceUpdateKey(prev => prev + 1);
        }, 50),
        setTimeout(() => {
          setForceUpdateKey(prev => prev + 1);
        }, 150),
        setTimeout(() => {
          setForceUpdateKey(prev => prev + 1);
        }, 300),
      ];
      return () => {
        timers.forEach(clearTimeout);
      };
    }
  }, [normalizedValue, currentCountry]);

  // Используем нормализованное значение для отображения
  const phoneValue = normalizedValue;

  // Создаем стабильную функцию для onChange, которая не меняется при изменении handleChange
  const safeHandleChangeRef = React.useRef(handleChange);
  React.useEffect(() => {
    safeHandleChangeRef.current = handleChange;
  }, [handleChange]);

  const safeHandleChange = React.useCallback((newValue, countryData) => {
    const handler = safeHandleChangeRef.current;
    if (typeof handler === 'function') {
      handler(newValue, countryData);
    }
  }, []);

  return (
    <TextInput
      label={label}
      error={error}
      helperText={helperText}
      checking={checking}
      onBlur={onBlur}
      onChange={safeHandleChange}
      inputProps={{
        inputComponent: MuiInputComponent,
        inputProps: {
          international: true,
          labels: localeRU,
          countryCallingCodeEditable: false,
          defaultCountry: currentCountry,
          country: currentCountry || 'RU',
          value: phoneValue,
          normalizedValue: phoneValue,
          forceUpdateKey: forceUpdateKey,
          initialValueKey: initialValueKey,
          onChange: safeHandleChange,
          className: classes.input,
          preferredCountries: PHONE_INPUT_PROPS?.preferredCountries,
          ...restProps
        }
      }}
    />
  );
};

RawPhoneInput.defaultProps = {};

RawPhoneInput.propTypes = {};

export const PhoneInput = RawPhoneInput;
